import {
  Box,
  Button,
  Typography,
  Grid,
  TextField,
  CircularProgress,
  Link as MuiLink,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import {
  useUserContext,
  useUserContextDispatch,
} from "../../../hooks/userContext";
import { useResendConfirmationEmail } from "../../../hooks/use-resend-confirmation-email";

// OPTIONAL: you need to implement this if not already available
const refreshUserContext = async () => {
  // Example: await refetchUser(); OR call to `/me` to rehydrate session info
};

export const VerificationCodeFieldset = ({
  isValid,
  step,
  setNextStep,
  setPreviousStep,
  setStep,
  setVerificationCode,
  verificationCode,
  email,
  userAccountId,
  setUserAccountId,
  phoneNumber,
}: {
  isValid: boolean;
  step: number;
  setNextStep: () => void;
  setPreviousStep: () => void;
  setStep: (value: number) => void;
  setVerificationCode: (value: string) => void;
  verificationCode?: string;
  email?: string;
  userAccountId?: string | null;
  setUserAccountId?: (value: string | null) => void;
  phoneNumber?: string;
}) => {
  const [error, setError] = useState<string | null>(null);
  const [codeDigits, setCodeDigits] = useState<string[]>(["", "", "", "", ""]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSentResumeCode, setHasSentResumeCode] = useState(false);
  const [resendCooldown, setResendCooldown] = useState<number>(0); // Countdown in seconds
  const [lastCodeSentTime, setLastCodeSentTime] = useState<number | null>(null);

  const { user } = useUserContext();
  const { confirmUserEmail } = useUserContextDispatch();
  const { handleResendConfirmationEmail, loading: resendLoading } = useResendConfirmationEmail();
  
  // Get phone number from props, user context, or user object - recalculate when dependencies change
  const displayPhoneNumber = useMemo(() => {
    return phoneNumber || (user as any)?.phoneNumber || undefined;
  }, [phoneNumber, user]);

  const inputRefs = useRef<HTMLInputElement[]>([]);

  // Create a stable callback for resending confirmation email
  const resendConfirmationEmail = useCallback(async (emailToSend: string, phoneToSend?: string) => {
    // Validate email before sending
    if (!emailToSend || emailToSend.trim() === '') {
      return;
    }
    
    
    try {
      const result = await handleResendConfirmationEmail(emailToSend, phoneToSend);
      
      if (result?.error) {
      } else {
      }
    } catch (error) {
    }
  }, [handleResendConfirmationEmail]);

  // Countdown timer for resend cooldown (90 seconds)
  // Updates every second based on when the last code was sent
  useEffect(() => {
    if (lastCodeSentTime) {
      const updateCooldown = () => {
        const elapsed = Math.floor((Date.now() - lastCodeSentTime) / 1000);
        const remaining = Math.max(0, 90 - elapsed);
        setResendCooldown(remaining);
        // Stop interval when countdown reaches 0
        if (remaining <= 0) {
          return true; // Signal to stop
        }
        return false;
      };

      // Update immediately
      updateCooldown();

      // Update every second while cooldown is active
      const interval = setInterval(() => {
        const shouldStop = updateCooldown();
        if (shouldStop) {
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    } else {
      // No code sent yet, ensure cooldown is 0
      setResendCooldown(0);
    }
  }, [lastCodeSentTime]);

  // Handle resume registration case
  // Use ref to prevent multiple sends and avoid triggering on every render
  const hasSentResumeCodeRef = useRef(false);
  const resumeCodeSentRef = useRef(false);
  
  useEffect(() => {
    // Only run once when component mounts or when userAccountId/email actually changes
    if (userAccountId === 'resume-registration' && email && email.trim() !== '' && !resumeCodeSentRef.current) {
      
      // Mark as sent immediately to prevent multiple calls
      resumeCodeSentRef.current = true;
      hasSentResumeCodeRef.current = true;
        setHasSentResumeCode(true);
      
      // Set lastCodeSentTime IMMEDIATELY so countdown timer works correctly
      const now = Date.now();
      setLastCodeSentTime(now);
      setResendCooldown(90); // Start 90-second cooldown immediately
      
      // Call resendConfirmationEmail immediately (ref prevents multiple calls)
      resendConfirmationEmail(email, displayPhoneNumber).catch((error) => {
      });
    } else if (userAccountId === 'resume-registration' && !email && !resumeCodeSentRef.current) {
    }
    // Only depend on the actual values we need, not computed values that change on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAccountId, email]);

  const handleChange = (index: number, value: string) => {
    const updated = [...codeDigits];
    const chars = value.toUpperCase().replace(/[^A-Z0-9]/gi, "").split("").slice(0, 5);

    if (!value) {
      updated[index] = "";
      setCodeDigits(updated);
      setVerificationCode(updated.join(""));
      return;
    }

    if (chars.length === 1) {
      updated[index] = chars[0];
      setCodeDigits(updated);
      setVerificationCode(updated.join(""));
      if (index < 4) inputRefs.current[index + 1]?.focus();
    } else if (chars.length === 5) {
      setCodeDigits(chars);
      setVerificationCode(chars.join(""));
      inputRefs.current[4]?.blur();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !codeDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (code: string) => {
    if (isSubmitting) {
      return; // Prevent multiple submissions
    }
    
    setIsSubmitting(true);
    // Pass the email for resume registration flow
    const emailToUse = userAccountId === 'resume-registration' ? email : undefined;
    const result = await confirmUserEmail(code, emailToUse);
    setIsSubmitting(false);

    if (typeof result === "object" && result.error) {
      setError(result.error as string);
    } else if (!result) {
      setError("The code was invalid. Please try again or resend.");
    } else {
      // After successful verification, ensure userAccountId is set
      // Result is now the user ID (string) if successful
      // This handles both new registrations and resume registration
      if (setUserAccountId) {
        // The backend now returns the user ID directly after email verification
        if (typeof result === 'string' && result !== 'resume-registration') {
          setUserAccountId(result);
        } else {
          // Fallback: try to get user ID from user context
          // Wait a moment for the context to update, then get the user ID
          let attempts = 0;
          const maxAttempts = 5;
          const checkUserId = () => {
            attempts++;
            const userId = user?.id || (user as any)?._id || (user as any)?.userId;
            if (userId && userId !== 'resume-registration') {
              setUserAccountId(userId);
            } else if (attempts < maxAttempts) {
              setTimeout(checkUserId, 200);
            } else {
              // If user ID is still not available, try to get it from localStorage
              const localUser = localStorage.getItem('user');
              if (localUser) {
                try {
                  const parsedUser = JSON.parse(localUser);
                  const localUserId = parsedUser?.id || parsedUser?._id || parsedUser?.userId;
                  if (localUserId && localUserId !== 'resume-registration') {
                    setUserAccountId(localUserId);
                  }
                } catch (e) {
                }
              }
            }
          };
          setTimeout(checkUserId, 100);
        }
      } else {
      }
      setNextStep();
    }
  };

  const handleResend = async () => {
    // Prevent resend if cooldown is active
    if (resendCooldown > 0) {
      return;
    }

    const emailToUse = userAccountId === 'resume-registration' ? email : user?.email;
    
    if (!emailToUse) {
      return;
    }

    if (resendLoading) {
      return; // Prevent multiple calls
    }

    const phoneToUse = phoneNumber || displayPhoneNumber;
    
    // Start cooldown immediately
    const now = Date.now();
    setLastCodeSentTime(now);
    setResendCooldown(90); // Set immediately for instant UI feedback
    
    await resendConfirmationEmail(emailToUse, phoneToUse);
    await refreshUserContext(); // ensure backend & frontend stay in sync

    // Clear input + error, refocus first box
    setCodeDigits(["", "", "", "", ""]);
    setVerificationCode("");
    setError(null);
    inputRefs.current[0]?.focus();
  };

  return (
    <Box
      sx={{
        maxWidth: 480,
        mx: "auto",
        px: 3,
        py: 6,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Typography variant="h4" fontWeight="bold" align="center" gutterBottom>
        {displayPhoneNumber ? 'Verify Your Email or Phone' : 'Verify Your Email'}
      </Typography>

      <Typography variant="body1" align="center" sx={{ mb: 2 }}>
        {userAccountId === 'resume-registration' ? (
          <>
            We've sent a new verification code to your {displayPhoneNumber ? 'email and phone number' : 'email'} to continue your registration.
          </>
        ) : (
          <>
            We've sent a verification code to your {displayPhoneNumber ? 'email and phone number' : 'email'}.
            <br />
            Please enter the code below to continue.
          </>
        )}
      </Typography>

      {/* Display the email address and phone number */}
      {(email || user?.email) && (
        <Box
          sx={{
            mb: 4,
            p: 2,
            bgcolor: '#F9FAFB',
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
          }}
        >
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 0.5 }}>
            Verification code sent to:
          </Typography>
          <Typography variant="body1" fontWeight={600} align="center" sx={{ color: '#1F2937' }}>
            {email || user?.email}
          </Typography>
          {displayPhoneNumber && (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
              and {displayPhoneNumber}
            </Typography>
          )}
        </Box>
      )}

      <Typography variant="subtitle1" fontWeight={600} align="center" sx={{ mb: 1 }}>
        Enter verification code
      </Typography>

      <Grid container spacing={2} justifyContent="center">
        {codeDigits.map((digit, i) => (
          <Grid item key={i}>
            <TextField
              inputRef={(el) => (inputRefs.current[i] = el!)}
              value={digit || ""}
              onChange={(e) => handleChange(i, e.target.value)}
              onPaste={(e) => {
                const pasted = e.clipboardData.getData("Text");
                handleChange(i, pasted);
                e.preventDefault();
              }}
              onKeyDown={(e) => handleKeyDown(i, e)}
              variant="outlined"
              inputProps={{
                inputMode: "text",
                maxLength: 1,
                style: {
                  textAlign: "center",
                  width: 30,
                  height: 30,
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  borderRadius: "12px",
                  textTransform: "uppercase",
                },
              }}
            />
          </Grid>
        ))}
      </Grid>

      <Button
        fullWidth
        variant="contained"
        size="large"
        sx={{
          mt: 4,
          py: 1.75,
          fontWeight: "bold",
          fontSize: "1rem",
          borderRadius: "10px",
        }}
        disabled={codeDigits.join("").length < 5 || isSubmitting}
        onClick={() => handleSubmit(codeDigits.join(""))}
      >
        {isSubmitting ? <CircularProgress size={20} sx={{ color: '#00A06D' }} /> : "Verify"}
      </Button>

      {error && (
        <Typography color="error" align="center" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}

      <Typography align="center" sx={{ mt: 2 }}>
        Didn't receive the code?{" "}
        {(() => {
          // Debug: log countdown state
          if (resendCooldown > 0) {
          }
          return resendCooldown > 0 ? (
            <Typography component="span" sx={{ fontWeight: 600, color: '#666' }}>
              Resend available in {resendCooldown}s
            </Typography>
          ) : (
        <MuiLink 
          component="button" 
          onClick={handleResend} 
              disabled={resendLoading || resendCooldown > 0}
          sx={{ 
            fontWeight: 600, 
                color: (resendLoading || resendCooldown > 0) ? '#ccc' : '#4F46E5',
                cursor: (resendLoading || resendCooldown > 0) ? 'not-allowed' : 'pointer',
                textDecoration: 'none'
          }}
        >
          {resendLoading ? 'Sending...' : 'Resend'}
        </MuiLink>
          );
        })()}
      </Typography>

      {/* NEW: gentle reminder under the resend link */}
      {!displayPhoneNumber && (
        <Typography align="center" sx={{ mt: 1, color: 'text.secondary' }}>
          Please check your spam/junk folder!
        </Typography>
      )}
      
      <Typography variant="body2" align="center" sx={{ mt: 1, color: '#666' }}>
        Resuming registration? The code may have expired. Click "Resend" above.
      </Typography>

      <Button
        startIcon={<ArrowBackIcon />}
        sx={{
          mt: 4,
          fontWeight: 600,
          justifyContent: "flex-start",
          textTransform: "none",
        }}
        onClick={setPreviousStep}
      >
        Back
      </Button>

      <Typography variant="caption" align="center" display="block" sx={{ mt: 5 }}>
        By Signing up you agree to our{" "}
        <MuiLink href="/terms" target="_blank">
          Terms
        </MuiLink>{" "}
        and{" "}
        <MuiLink href="/privacy" target="_blank">
          Privacy Policy
        </MuiLink>
        .
      </Typography>
    </Box>
  );
};
